import { formatDateLocalized } from '@gripp/shared-logic';

// Shared helpers for serializing routine submission answers into CSV cells.
// Extracted from downloadRoutine.tsx so the data-plate export hook can reuse
// them without depending on the routine-export component (avoids a cycle).

export const RESPONSE_VALUE_KEYS = [
  'value',
  'confirmedValue',
  'count',
  'status',
  'option',
  'options',
  'mediaIds',
  'timeTypeValue',
  'barcodes',
] as const;

export type ResponseValueKey = (typeof RESPONSE_VALUE_KEYS)[number];

// Response value keys whose answers are lists — these are the "multiples"
// (barcode scans, images, multi-selects) that split mode spreads across one
// column per value instead of comma-joining into a single cell. Single-value
// keys (option, value, status, …) are never split.
export const MULTI_VALUE_KEYS = new Set<ResponseValueKey>([
  'barcodes',
  'mediaIds',
  'options',
]);

export type BarcodeExportEntry = {
  value?: unknown;
  quantity?: unknown;
};

// react-csv 2.x wraps values in double-quotes but does NOT escape inner double-quotes,
// producing malformed CSV when values contain characters like inch marks (e.g. .010").
// Sanitize all string output to avoid breaking column alignment in the exported file.
export function sanitizeCsvString(value: string): string {
  return value.replace(/"/g, "'");
}

function formatSingleBarcode(entry: BarcodeExportEntry): string {
  if (!entry || typeof entry !== 'object') {
    return '';
  }

  const barcodeValue = typeof entry.value === 'string' ? entry.value : '';
  if (!barcodeValue) {
    return '';
  }

  return entry.quantity !== undefined
    ? `${barcodeValue} (${entry.quantity})`
    : barcodeValue;
}

function formatBarcodeEntries(value: BarcodeExportEntry[]): string {
  return sanitizeCsvString(
    value.map(formatSingleBarcode).filter(Boolean).join(', ')
  );
}

/**
 * Splits a single response into its individual scalar values for "split to rows"
 * mode. Multi-value answers (barcodes, images/mediaIds, multi-select options)
 * yield one entry each; single-value answers yield exactly one (reusing
 * formatResponseValue so timeType/open-ended formatting stays consistent).
 */
export function extractIndividualValues(
  response: Record<string, unknown>,
  valueKey?: ResponseValueKey
): string[] {
  if (!valueKey) {
    return [];
  }

  const value = response[valueKey];

  if (valueKey === 'barcodes' && Array.isArray(value)) {
    return value
      .map((entry) => formatSingleBarcode(entry as BarcodeExportEntry))
      .filter(Boolean)
      .map(sanitizeCsvString);
  }

  if (Array.isArray(value)) {
    return value
      .map((item) =>
        typeof item === 'string' ? sanitizeCsvString(item) : String(item)
      )
      .filter((item) => item !== '');
  }

  const formatted = formatResponseValue(response, valueKey);
  if (formatted === '' || formatted === undefined || formatted === null) {
    return [];
  }

  return [formatted as string];
}

export function getResponseKey(
  response: Record<string, unknown>
): ResponseValueKey | undefined {
  const keys = Object.keys(response);
  return keys.find((key): key is ResponseValueKey =>
    RESPONSE_VALUE_KEYS.includes(key as ResponseValueKey)
  );
}

export function formatResponseValue(
  response: Record<string, unknown>,
  valueKey?: ResponseValueKey
) {
  if (!valueKey) {
    return '';
  }

  const value = response[valueKey];

  if (valueKey === 'barcodes' && Array.isArray(value)) {
    return formatBarcodeEntries(value as BarcodeExportEntry[]);
  }

  if (Array.isArray(value)) {
    return sanitizeCsvString(value.join(', '));
  }

  if (value && valueKey === 'timeTypeValue') {
    if (
      typeof value === 'object' &&
      'startTime' in value &&
      'endTime' in value
    ) {
      return `${formatDateLocalized(
        (value as { startTime: string }).startTime
      )} - ${formatDateLocalized((value as { endTime: string }).endTime)}`;
    } else if (typeof value === 'string') {
      if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
        return formatDateLocalized(value);
      }
      return value;
    }
  } else if (
    valueKey === 'status' &&
    value === 'openEndedResponse' &&
    response.openEndedResponse
  ) {
    return sanitizeCsvString(response.openEndedResponse as string);
  }

  if (typeof value === 'string') {
    return sanitizeCsvString(value);
  }

  return value;
}
